/**
 * Runtime validation for every REST body and socket payload. SPEC.md §38.
 *
 * The server never trusts a browser payload, and the client reuses these
 * schemas so a malformed request is caught before it leaves the device.
 */

import { z } from 'zod';
import {
  IDEMPOTENCY_KEY_MAX_LENGTH,
  IDEMPOTENCY_KEY_MIN_LENGTH,
  MAX_POINTS_PER_DRINK,
  MAX_TRANSFER_AMOUNT,
  MIN_POINTS_PER_DRINK,
  MIN_TRANSFER_AMOUNT,
  PLAYER_NAME_MAX_LENGTH,
  PLAYER_NAME_MIN_LENGTH,
  isKnownAvatarKey,
} from './constants.js';
import { DomainError } from './errors.js';
import { isValidJoinCode, normalizeJoinCode } from './joincode.js';

// --- Primitives --------------------------------------------------------------

export const idSchema = z.uuid();

export const joinCodeSchema = z
  .string()
  .min(1)
  .max(32)
  .transform(normalizeJoinCode)
  .refine(isValidJoinCode, { message: 'Enter a valid table code.' });

export const playerNameSchema = z
  .string()
  .trim()
  .min(PLAYER_NAME_MIN_LENGTH, { message: 'Please enter a name.' })
  .max(PLAYER_NAME_MAX_LENGTH, { message: `Names are capped at ${PLAYER_NAME_MAX_LENGTH} characters.` });

export const avatarKeySchema = z
  .string()
  .refine(isKnownAvatarKey, { message: 'Pick one of the available avatars.' });

export const amountSchema = z
  .number()
  .int()
  .min(MIN_TRANSFER_AMOUNT)
  .max(MAX_TRANSFER_AMOUNT);

export const idempotencyKeySchema = z
  .string()
  .min(IDEMPOTENCY_KEY_MIN_LENGTH)
  .max(IDEMPOTENCY_KEY_MAX_LENGTH);

export const playerTokenSchema = z.string().min(32).max(256);

/** What a reported loss does to the rest of the table. §3 */
export const entryShapeSchema = z.enum(['TO_WINNER', 'TO_EVERYONE']);

export const seatIndexSchema = z.number().int().min(0).max(63);

/** Non-negative sequence; 0 means "I have no state yet". §15.2 */
export const sequenceSchema = z.number().int().min(0);

// --- Identity ----------------------------------------------------------------

export const playerIdentitySchema = z.object({
  name: playerNameSchema,
  avatarKey: avatarKeySchema,
});
export type PlayerIdentityInput = z.infer<typeof playerIdentitySchema>;

// --- REST bodies -------------------------------------------------------------

/**
 * What a cup is worth. Chosen once, when the table is created. §4.6
 *
 * The message is written for a player because it is the one number on the create
 * screen somebody types by hand, and "Invalid input" beside it would be a
 * developer talking to a host. §35
 */
export const pointsPerDrinkSchema = z
  .number()
  .int()
  .min(MIN_POINTS_PER_DRINK, { message: 'A cup has to be worth at least 1 point.' })
  .max(MAX_POINTS_PER_DRINK, {
    message: `A cup is capped at ${MAX_POINTS_PER_DRINK} points.`,
  });

/**
 * POST /api/sessions — the identity, plus the mode the whole night is played in.
 *
 * The two drinking fields travel together and are refined as a pair: a table in
 * drinking mode without a price for a cup has no way to credit one, and a price
 * on a table that is not in drinking mode is a number nothing would ever read.
 * §4.6
 */
export const createSessionBodySchema = playerIdentitySchema
  .extend({
    drinkingMode: z.boolean().optional(),
    pointsPerDrink: pointsPerDrinkSchema.optional(),
  })
  .refine((body) => !body.drinkingMode || body.pointsPerDrink !== undefined, {
    message: 'Say how many points a cup is worth.',
    path: ['pointsPerDrink'],
  });
export type CreateSessionBody = z.infer<typeof createSessionBodySchema>;

/** POST /api/sessions/:joinCode/join */
export const joinSessionBodySchema = playerIdentitySchema;
export type JoinSessionBody = z.infer<typeof joinSessionBodySchema>;

/** POST /api/sessions/:sessionId/reconnect */
export const reconnectBodySchema = z.object({
  playerToken: playerTokenSchema,
});
export type ReconnectBody = z.infer<typeof reconnectBodySchema>;

// --- Socket: client -> server ------------------------------------------------

/**
 * Subscribes a socket to a session room. Authorization is the player token —
 * a join code is never an authorization credential. §27
 */
export const attachSessionInputSchema = z.object({
  sessionId: idSchema,
  playerToken: playerTokenSchema,
  /** Last sequence this client applied, so the server can skip a full snapshot. */
  lastSequence: sequenceSchema.optional(),
});
export type AttachSessionInput = z.infer<typeof attachSessionInputSchema>;

export const updateProfileInputSchema = z.object({
  name: playerNameSchema.optional(),
  avatarKey: avatarKeySchema.optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileInputSchema>;

export const moveSeatInputSchema = z.object({
  /** Omitted or self for a normal player; any player for the host. §9.3 */
  playerId: idSchema.optional(),
  toSeatIndex: seatIndexSchema,
});
export type MoveSeatInput = z.infer<typeof moveSeatInputSchema>;

/**
 * Opens the next hand. It carries nothing: there is no mode to choose and no
 * player to designate, because both are read back out of what the table
 * reports. §3, §4.3
 */
export const startRoundInputSchema = z.object({});
export type StartRoundInput = z.infer<typeof startRoundInputSchema>;

export const completeRoundInputSchema = z.object({
  roundId: idSchema,
});
export type CompleteRoundInput = z.infer<typeof completeRoundInputSchema>;

/**
 * Deletes a hand so the table can play it again. §12.5
 *
 * Hands close themselves now, so a table that reported the wrong thing has no
 * report left to withdraw — this is the way back. It is a host power because it
 * takes points off everybody at once, and it is a void rather than a removal:
 * the reports go, the rows they derived are cancelled with inverses, and the
 * hand stays in history carrying both halves. §12.2, §5.1
 */
export const voidRoundInputSchema = z.object({
  roundId: idSchema,
});
export type VoidRoundInput = z.infer<typeof voidRoundInputSchema>;

/**
 * Reports this player's loss for the hand. The client sends the magnitude and
 * what it does to the rest of the table — never a resulting total, never the
 * transfers, and never who won. §15.1, §3.1
 *
 * Reporting again replaces the standing entry rather than adding to it, so a
 * corrected amount is one call and not an undo followed by a re-entry. §12.2
 */
export const setHandEntryInputSchema = z.object({
  roundId: idSchema,
  amount: amountSchema,
  shape: entryShapeSchema,
  idempotencyKey: idempotencyKeySchema,
  /** Set when the host is entering on someone's behalf. §30 */
  actorPlayerId: idSchema.optional(),
});
export type SetHandEntryInput = z.infer<typeof setHandEntryInputSchema>;

/**
 * Withdraws this player's report for the hand. Used by the snackbar's UNDO and
 * by the keypad's Resubmit — the control is named for what a player wants from
 * it (their number back on the keypad), and this is what that costs: the report
 * goes, and with it every row it had derived. It is the only undo there is,
 * because a transfer is derived rather than entered and cancelling one on its
 * own would simply be re-derived on the next keystroke. §12.2, §12.3
 */
export const clearHandEntryInputSchema = z.object({
  roundId: idSchema,
  idempotencyKey: idempotencyKeySchema,
  actorPlayerId: idSchema.optional(),
});
export type ClearHandEntryInput = z.infer<typeof clearHandEntryInputSchema>;

/**
 * "Finished your drink?" — yes. §4.6
 *
 * It carries no amount: what a cup is worth was fixed when the table was
 * created, and a client that could name its own figure would be a client that
 * could award itself any number of points. The idempotency key is what stops a
 * packet Socket.IO re-sends after a reconnect from crediting the drink twice.
 * §12.4, §15.1
 */
export const recordDrinkInputSchema = z.object({
  roundId: idSchema,
  idempotencyKey: idempotencyKeySchema,
  /** Set when the host is recording it for someone. §30 */
  actorPlayerId: idSchema.optional(),
});
export type RecordDrinkInput = z.infer<typeof recordDrinkInputSchema>;

export const removePlayerInputSchema = z.object({
  playerId: idSchema,
});
export type RemovePlayerInput = z.infer<typeof removePlayerInputSchema>;

/**
 * The host names who deals the hand in progress. §9.6
 *
 * A player, not an offset: the wire carries the intent and the server derives
 * the number that produces it, so a client can never store a count that means
 * something different on the seating the server actually has. §60
 */
export const selectDealerInputSchema = z.object({
  /**
   * The hand the host is looking at. §9.6, §12.4
   *
   * Carried for the reason every other round-scoped mutation carries it: hands
   * close themselves, so the round that is active when the tap lands may not be
   * the round the host was looking at when they made it. Without this, a pick
   * made as the hand ends silently re-anchors the *next* one.
   */
  roundId: idSchema,
  playerId: idSchema,
});
export type SelectDealerInput = z.infer<typeof selectDealerInputSchema>;

export const setLockedInputSchema = z.object({
  locked: z.boolean(),
});
export type SetLockedInput = z.infer<typeof setLockedInputSchema>;

export const emptyInputSchema = z.object({}).optional();

// --- Helpers -----------------------------------------------------------------

/**
 * Parses with a schema and rethrows as a domain validation error so no Zod
 * internals or field paths reach the UI. §35
 */
export function parseOrThrow<T>(schema: z.ZodType<T>, value: unknown): T {
  const result = schema.safeParse(value);
  if (!result.success) {
    const first = result.error.issues[0];
    const detail = first ? `${first.path.join('.') || '(root)'}: ${first.message}` : 'invalid payload';
    throw new DomainError('VALIDATION_FAILED', detail);
  }
  return result.data;
}
